import { ITEM_DO_RODAPE_DA_GAVETA } from '@/components/layout/itemDaGaveta';
import { Link, useLocation } from 'react-router-dom';
import { SinoDeNotificacoes } from '@/features/notificacoes/SinoDeNotificacoes';
import SairDaConta from '@/components/layout/SairDaConta';
import { BotaoDeRelato } from '@/features/relatos/components/RelatarAlgo';
import { BotaoDeRecolherMenu, useMenuRecolhido } from '@/components/layout/menuRecolhido';
import { useCuradoriaAbertos, classeDoNumeroDaCuradoria } from '@/features/quiz/api/curadoriaAbertos';
import { plural, palavraNoPlural } from '@/utils/plural';
import {
    HomeIcon,
    BookOpenIcon,
    QuestionMarkCircleIcon,
    BuildingOfficeIcon,
    UserGroupIcon,
    UsersIcon,
    ArrowUpTrayIcon,
    ChatBubbleLeftEllipsisIcon,
    UserPlusIcon,
    BeakerIcon,
    ChartBarIcon,
    ExclamationTriangleIcon,
    ScaleIcon,
} from '@heroicons/react/24/outline';

interface AdminSidebarProps {
    variant?: 'sidebar' | 'drawer';
    onNavigate?: () => void;
}

const SECOES_DA_GAVETA = [
    { titulo: 'Dia a dia', caminhos: ['/admin', '/admin/pedidos', '/admin/relatos', '/admin/users', '/admin/uso'] },
    // "Classificação" (26/10/2026): Disciplinas, Conteúdos e Habilidades em abas — a mesma página do professor.
    { titulo: 'Acervo', caminhos: ['/admin/questions', '/admin/classificacao', '/admin/curadoria', '/admin/importacoes'] },
    { titulo: 'Escolas', caminhos: ['/admin/schools', '/admin/classes'] },
    { titulo: 'Ferramentas', caminhos: ['/admin/rasch', '/admin/bateria-omr'] },
];

const AdminSidebar = ({ variant = 'sidebar', onNavigate }: AdminSidebarProps) => {
    const location = useLocation();
    // Recolhida só a barra FIXA: a gaveta do celular nunca vira faixa de ícones (ver `menuRecolhido`).
    const recolhido = useMenuRecolhido() && variant === 'sidebar';

    // Também nas páginas de dentro (26/10/2026): a turma, a questão, a aba da classificação acendem o item. O Painel, só ele.
    const isActive = (path: string) => {
        if (path === '/admin') return location.pathname === path;
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };
    const curadoriaAbertos = useCuradoriaAbertos();

    const menuItems = [
        { path: '/admin', label: 'Painel', icon: HomeIcon },
        { path: '/admin/classificacao', label: 'Classificação', icon: BookOpenIcon },
        { path: '/admin/questions', label: 'Questões', icon: QuestionMarkCircleIcon },
        { path: '/admin/schools', label: 'Escolas', icon: BuildingOfficeIcon },
        { path: '/admin/classes', label: 'Turmas', icon: UserGroupIcon },
        { path: '/admin/users', label: 'Usuários', icon: UsersIcon },
        // O custo da API é do pesquisador, não da escola nem do professor — por
        // isso o histórico das importações vive aqui, e não em /gestao.
        { path: '/admin/importacoes', label: 'Importações', icon: ArrowUpTrayIcon },
        // A fila dos relatos sobre o PRODUTO — o que chegou pelo botão "Relatar
        // algo". Não é a curadoria do acervo: aquela fala de questões e tem
        // plateia distribuída; esta fala do portal e tem plateia de um.
        // No fim por acréscimo, como os anteriores: o menu cresce sem rearranjar.
        { path: '/admin/relatos', label: 'Relatos', icon: ChatBubbleLeftEllipsisIcon },
        // Os pedidos de acesso de quem não é da rede estadual de Alagoas (28/09/2026).
        { path: '/admin/pedidos', label: 'Pedidos de acesso', icon: UserPlusIcon },
        // Os motores OMR do iOS e do Android lendo o mesmo acervo — a única forma de
        // compará-los (o iOS não guarda a foto que leu, achado 182).
        { path: '/admin/bateria-omr', label: 'Bateria OMR', icon: BeakerIcon },
        // O uso do portal por semana (02/10/2026) — só com o que o banco já guarda.
        { path: '/admin/uso', label: 'Painel de uso', icon: ChartBarIcon },
        // A fila de curadoria: é aqui que chega a proposta de correção de gabarito de um autor.
        { path: '/admin/curadoria', label: 'Curadoria', icon: ExclamationTriangleIcon },
        // O Rasch no portal (C3a, 17/10/2026): a régua por disciplina, em testes; o professor não vê nada nesta etapa.
        { path: '/admin/rasch', label: 'Rasch (em testes)', icon: ScaleIcon },
    ];

    // `h-full` pelo mesmo motivo do `TeacherSidebar` (achado 96): o invólucro do
    // `AdminLayout` também é `flex-1 overflow-hidden`, e sem altura presa esta
    // raiz cresce até o conteúdo e o excedente é aparado em silêncio. Aqui o
    // defeito estava LATENTE — a lista do administrador é curta o bastante para
    // caber —, e corrigido junto para não esperar o item que a fizer transbordar.
    // NA GAVETA, UMA LISTA SÓ QUE ROLA INTEIRA (24/10/2026, pedido do pesquisador — "dois blocos se sobrepõem"): antes o
    // `<nav>` rolava por dentro e o rodapé (Notificações, Relatar algo, Minha conta, Trocar de conta, Sair) ficava FIXO
    // embaixo, com ~250 px. Num celular de 780 px a navegação ficava cortada rente ao rodapé, sem divisória nem sinal de
    // que havia mais — os itens pareciam um por cima do outro. Agora a ordem é a da leitura: navegação → Notificações e
    // Relatar algo → Minha conta, Trocar de conta → Sair, o último; e quem rola é a gaveta. A barra do computador não muda.
    const rootClass = variant === 'drawer'
        ? 'w-full h-full bg-white min-h-0 flex flex-col overflow-y-auto overscroll-contain'
        // O desktop levava o achado 90 em aberto: `h-screen` sem `flex flex-col`
        // deixa o `flex-1 overflow-y-auto` do `<nav>` inerte — fora de um flex
        // container, `flex-1` não faz nada. Corrigido como no `TeacherSidebar`.
        // `max-h-screen` e não `h-screen`, como no `TeacherSidebar`: altura do
        // conteúdo limitada à janela, em vez de 100vh forçados a partir de um
        // ponto que não é o topo dela.
        : `${recolhido ? 'w-16' : 'w-64'} bg-white shadow-sm max-h-screen flex flex-col`;

    return (
        <div className={rootClass}>
            <div className={`flex-shrink-0 flex items-center ${recolhido ? 'justify-center p-2' : 'justify-between gap-2 p-4'}`}>
                {/* Na gaveta, sem o nome do portal repetido (a barra de cima já o tem) — como na do professor. */}
                {/* Sem o "Portal Simulados" (24/10/2026): a barra do topo já o tem. */}
                {!recolhido && variant === 'sidebar' && <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Menu</span>}
                {variant === 'sidebar' && <BotaoDeRecolherMenu recolhido={recolhido} />}
            </div>
            <nav className={`${recolhido ? 'mt-1' : variant === 'drawer' ? 'mt-0 flex-none' : 'mt-4 flex-1 overflow-y-auto'}`}>
                {/* NA GAVETA, EM SEÇÕES (23/10/2026, lote do administrador — o mesmo desenho das gavetas do professor e da
                    gestão). A barra lateral do computador segue com a lista de sempre. */}
                {/* EM SEÇÕES, NA GAVETA E NA BARRA DO COMPUTADOR (24/10/2026, revisão do pesquisador). Recolhida: só os ícones. */}
                <div data-testid={variant === 'drawer' ? 'gaveta-em-secoes' : 'menu-em-secoes'}>
                    {SECOES_DA_GAVETA.map((secao) => (
                        <div key={secao.titulo} className={recolhido ? 'mt-2 border-t border-gray-100 pt-2 first:mt-0 first:border-0 first:pt-0' : 'mb-2'}>
                            {!recolhido && <p className="px-4 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{secao.titulo}</p>}
                            {secao.caminhos.map((caminho) => {
                                const item = menuItems.find((m) => m.path === caminho);
                                if (!item) return null;
                                const Icon = item.icon;
                                return (
                                    <Link key={caminho} to={caminho} onClick={onNavigate} title={recolhido ? item.label : undefined} aria-current={isActive(caminho) ? 'page' : undefined} data-testid={`menu-${caminho}`}
                                        className={`relative flex min-h-[44px] items-center ${recolhido ? 'justify-center px-0' : 'px-4'} py-2 text-sm ${isActive(caminho) ? 'bg-marca-50 text-marca-600' : 'text-gray-600 hover:bg-gray-50'}`}>
                                        <Icon className={`h-5 w-5 flex-shrink-0 ${recolhido ? '' : 'mr-3'}`} />
                                        <span className={recolhido ? 'sr-only' : undefined}>{item.label}</span>
                                        {caminho === '/admin/curadoria' && curadoriaAbertos > 0 && (
                                            <span className={classeDoNumeroDaCuradoria(recolhido)} data-testid="numero-da-curadoria" title={recolhido ? plural(curadoriaAbertos, 'achado aberto', 'achados abertos') : undefined}>
                                                {recolhido ? <span className="sr-only">{plural(curadoriaAbertos, 'achado aberto', 'achados abertos')}</span> : curadoriaAbertos}
                                                {!recolhido && <span className="sr-only"> {palavraNoPlural(curadoriaAbertos, 'achado aberto', 'achados abertos')}</span>}
                                            </span>
                                        )}
                                    </Link>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </nav>
            {/* Sair só na GAVETA — no desktop a barra do topo do `MainLayout`
                já o traz. Irmão do `<nav>`, para não descer com a rolagem. */}
            {/*
                O CANAL DE RELATO NO CELULAR.
                A barra do topo do `MainLayout` traz o botão, mas ela é
                `hidden md:flex` — abaixo de 768px ele some. E a gaveta do
                `MainLayout` não abre aqui, porque esta rota tem gaveta
                própria (`temGavetaPropria`). Sem esta linha, o professor no
                aplicativo ficava SEM canal em toda a área dele, que é onde
                ele passa o tempo todo.
            */}
            {variant === 'drawer' && (
              <div className="flex-shrink-0 px-2">
                <SinoDeNotificacoes variante="item" aoNavegar={onNavigate} />
              </div>
            )}
            {variant === 'drawer' && (
                <div className="flex-shrink-0 px-2 pb-1">
                    <BotaoDeRelato className={ITEM_DO_RODAPE_DA_GAVETA} />
                </div>
            )}
            {variant === 'drawer' && <SairDaConta aoSair={onNavigate} />}
        </div>
    );
};

export default AdminSidebar; 